"use client"

import { useState, useEffect } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { ShoppingCart, Loader2 } from "@/lib/icons"
import { formatPrice } from "@/lib/marketplace-store"
import type { UserScript } from "@/lib/script-store"
import { ScriptPlayerModal } from "@/components/script-player-modal"
import type { ScriptPromoAd } from "./script-promo-card"
import { useTranslations } from "next-intl"

interface ScriptTrialModalProps {
  promo: ScriptPromoAd | null
  onClose: () => void
  onPurchase: (promo: ScriptPromoAd) => void
}

// Real playback (Handy sync, heatmap, transport controls) happens in ScriptPlayerModal via
// trialMode — this component's job is just to fetch the trial content for an ad-campaign promo
// (which only carries a script_id, not the full script record) and hand it off, then show a
// "buy now" nudge once the trial naturally ends.
export function ScriptTrialModal({ promo, onClose, onPurchase }: ScriptTrialModalProps) {
  const t = useTranslations("scriptTrialModal")
  const [loading, setLoading] = useState(false)
  const [trialScript, setTrialScript] = useState<UserScript | null>(null)
  const [trialCutoffMs, setTrialCutoffMs] = useState<number | undefined>(undefined)
  const [playerOpen, setPlayerOpen] = useState(false)
  const [showBuyPrompt, setShowBuyPrompt] = useState(false)

  useEffect(() => {
    setTrialScript(null)
    setTrialCutoffMs(undefined)
    setPlayerOpen(false)
    setShowBuyPrompt(false)
    if (promo) fetchTrialContent(promo)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promo?.script_id])

  const fetchTrialContent = async (activePromo: ScriptPromoAd) => {
    setLoading(true)
    try {
      const trialRes = await fetch(`/api/marketplace/scripts/${activePromo.script_id}/trial`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ source_campaign_id: activePromo.campaign_id }),
      })
      const trialData = await trialRes.json()
      if (!trialRes.ok) throw new Error(trialData.error || "Failed to start trial")
      if (!trialData.script_content?.actions) throw new Error(t("trialContentUnavailable"))

      // Promo cards only carry script_id — best-effort fetch the full record for content fields
      // (linked video URL/type). Not fatal if it fails: preview_video_url is a working fallback.
      let scriptDetail: any = null
      try {
        const detailRes = await fetch(`/api/marketplace/scripts/${activePromo.script_id}`)
        if (detailRes.ok) scriptDetail = (await detailRes.json()).script
      } catch {}

      const durationMs = (trialData.trial_duration_seconds ?? activePromo.trial_duration_seconds ?? 30) * 1000
      const funscriptData = trialData.script_content

      const userScript: UserScript = {
        id: activePromo.script_id,
        name: activePromo.title,
        description: scriptDetail?.description ?? activePromo.description ?? "",
        contentUrl: scriptDetail?.linked_content_url ?? activePromo.preview_video_url ?? undefined,
        contentSite: scriptDetail?.linked_content_site ?? undefined,
        contentType: (scriptDetail?.content_type as any) ?? "video",
        thumbnailUrl: activePromo.thumbnail_url ?? undefined,
        funscript: {
          version: funscriptData.version ?? "1.0",
          inverted: false,
          range: 100,
          actions: funscriptData.actions ?? [],
          metadata: { title: activePromo.title, duration: funscriptData.metadata?.duration ?? durationMs },
        },
        isPublished: true,
        tags: activePromo.tags ?? [],
        deviceCompatibility: [],
        averageIntensity: scriptDetail?.average_intensity ?? 50,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      }

      setTrialScript(userScript)
      // Server recognized this user already owns the script — play in full, no cutoff.
      setTrialCutoffMs(trialData.is_purchased ? undefined : durationMs)
      setPlayerOpen(true)
    } catch (error) {
      console.error("Failed to fetch trial content:", error)
      onClose()
    } finally {
      setLoading(false)
    }
  }

  if (!promo) return null

  return (
    <>
      {loading && (
        <Dialog open onOpenChange={() => onClose()}>
          <DialogContent className="max-w-sm flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </DialogContent>
        </Dialog>
      )}

      <ScriptPlayerModal
        script={trialScript}
        open={playerOpen}
        onClose={() => { setPlayerOpen(false); onClose() }}
        trialMode
        previewCutoffMs={trialCutoffMs}
        onCutoffReached={() => { setPlayerOpen(false); setShowBuyPrompt(true) }}
      />

      <Dialog open={showBuyPrompt} onOpenChange={(v) => { if (!v) { setShowBuyPrompt(false); onClose() } }}>
        <DialogContent className="max-w-sm text-center">
          <DialogHeader>
            <DialogTitle className="text-center">{t("trialEnded")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{t("purchaseToContinueWithTheFullScript")}</p>
          <div className="flex gap-2 justify-center pt-2">
            <Button variant="outline" onClick={() => { setShowBuyPrompt(false); onClose() }}>
              {t("close")}
            </Button>
            <Button onClick={() => { setShowBuyPrompt(false); onPurchase(promo) }}>
              <ShoppingCart className="h-4 w-4 mr-2" />
              {t("buyNowValue", { value: formatPrice(promo.price_cents) })}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
